import type { ReactNode } from 'react';
import {
    Carousel,
    CarouselContent,
    CarouselPrevious,
    CarouselNext,
    type CarouselOptions,
} from '@rocco/components/carousel';
import { ReactComponent as ArrowLeft } from '@rocco/icons/svg/arrow/arrow-left.svg';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow/arrow-right.svg';
import { cn } from '@rocco/utils/cn';

interface CarouselSectionProps {
    /**
     * The title to display at the top of the carousel section
     */
    title: string;
    /**
     * The carousel items to render
     */
    children: ReactNode;
    /**
     * Optional carousel configuration options
     */
    options?: CarouselOptions;
    /**
     * Additional CSS classes for the container
     */
    className?: string;
    /**
     * Additional CSS classes for the carousel content
     */
    contentClassName?: string;
}

/**
 * A reusable carousel section component with title and navigation controls
 */
export const CarouselSection = ({
    title,
    children,
    options,
    className,
    contentClassName,
}: CarouselSectionProps) => {
    return (
        <div className={cn('md:pl-12', className)}>
            <Carousel options={options}>
                <div className="relative mb-4 flex items-center justify-between text-[#323232] md:mb-10">
                    <h2 className="text-9 font-medium -tracking-[0.025rem]">
                        {title}
                    </h2>
                    <div className="flex gap-4 pr-15 pt-2 md:min-h-10">
                        <CarouselPrevious className="md:size-11">
                            <ArrowLeft className="size-5" />
                        </CarouselPrevious>
                        <CarouselNext className="md:size-11">
                            <ArrowRight className="size-5" />
                        </CarouselNext>
                    </div>
                </div>
                <CarouselContent className={cn('md:gap-5', contentClassName)}>
                    {children}
                </CarouselContent>
            </Carousel>
        </div>
    );
};
